import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  createMyTicket,
  getMyEvents,
  getMyTickets,
} from '../../../requests/customerPortal';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../../Admin/ui';
import { fmtDateTime, LoadFailed, retryUnless4xx } from '../shared';

const AdminTickets: React.FC = () => {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get('new') === '1');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [eventId, setEventId] = useState(params.get('event') ?? '');
  const [done, setDone] = useState<string | null>(null);

  // Events on the account, for the optional "about which event?" pick.
  const { data: events } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'events', 'all', ''],
    queryFn: () => getMyEvents('all'),
    enabled: open,
  });

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'tickets'],
    queryFn: getMyTickets,
  });

  const create = useMutation({
    mutationFn: createMyTicket,
    onSuccess: (t) => {
      setDone(t.number);
      setSubject('');
      setDetails('');
      setEventId('');
      queryClient.invalidateQueries({ queryKey: ['my', 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my', 'overview'] });
    },
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  const openForm = () => {
    setDone(null);
    setOpen(true);
    setParams({ new: '1' });
  };
  const closeForm = () => {
    setOpen(false);
    setParams({});
  };

  return (
    <>
      <PageHeader
        title="Tickets"
        meta="Need help or a change? Open a ticket and you will hear back within one business day."
        actions={
          !open && (
            <button className={btnPrimary} onClick={openForm}>
              Submit a ticket
            </button>
          )
        }
      />
      <PageBody>
        {open && (
          <Card title="Quick connect" className="mb-6">
            {done ? (
              <div className="px-5 py-6">
                <div className="text-sm font-semibold text-gray-five">
                  Thank you — a case is open with our team.
                </div>
                <div className="mt-1 text-sm text-gray-three">
                  Your reference is <span className="font-medium">{done}</span>.
                  You will get a response in under one business day, usually
                  faster.
                </div>
                <div className="mt-4 flex gap-2">
                  <button className={btnSecondary} onClick={closeForm}>
                    Done
                  </button>
                  <button
                    className={btnSecondary}
                    onClick={() => setDone(null)}
                  >
                    Submit another
                  </button>
                </div>
              </div>
            ) : (
              <form
                className="grid gap-4 px-5 py-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  create.mutate({
                    subject: subject.trim(),
                    details: details.trim(),
                    eventId: eventId || undefined,
                  });
                }}
              >
                <div>
                  <label className={labelClass}>
                    What is the request regarding?
                  </label>
                  <input
                    className={`${inputClass} w-full`}
                    value={subject}
                    maxLength={200}
                    required
                    minLength={3}
                    placeholder="A short subject line"
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>Details of your request</label>
                  <textarea
                    className={`${inputClass} h-32 w-full py-2`}
                    value={details}
                    maxLength={5000}
                    required
                    minLength={3}
                    placeholder="Give us the context — who, what, when, and anything we should know."
                    onChange={(e) => setDetails(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    About which event? (optional)
                  </label>
                  <select
                    className={`${inputClass} w-full`}
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                  >
                    <option value="">Not about a specific event</option>
                    {(events?.items ?? []).map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name ?? 'Private event'} · {fmtDateTime(ev.start)}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-gray-bold">
                    Linking the event saves our team working out which class you
                    mean.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className={btnPrimary}
                    disabled={create.isPending}
                  >
                    {create.isPending ? 'Sending…' : 'Next'}
                  </button>
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={closeForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </Card>
        )}

        <Card title="Your tickets">
          {isLoading ? (
            <LoadingSpinner />
          ) : error || !data ? (
            <LoadFailed error={error} />
          ) : data.length === 0 ? (
            <EmptyState
              title="No tickets yet"
              action={
                <button className={btnPrimary} onClick={openForm}>
                  Submit a ticket
                </button>
              }
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Case</th>
                  <th className={thClass}>Subject</th>
                  <th className={thClass}>Event</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Opened</th>
                  <th className={thClass}>Closed</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t) => (
                  <tr key={t.id}>
                    <td
                      className={`${tdClass} whitespace-nowrap text-gray-bold`}
                    >
                      {t.number}
                      {t.mine && (
                        <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-primary">
                          you
                        </span>
                      )}
                    </td>
                    <td className={tdClass}>{t.subject ?? '—'}</td>
                    <td className={`${tdClass} max-w-[220px]`}>
                      {t.eventId ? (
                        <Link
                          className="block truncate text-blue-primary hover:underline"
                          to={`${PATH.adminEvents}?id=${t.eventId}`}
                          title={t.eventName ?? ''}
                        >
                          {t.eventName ?? 'Event'}
                        </Link>
                      ) : (
                        <span className="text-gray-two">—</span>
                      )}
                    </td>
                    <td className={tdClass}>
                      <Pill
                        value={t.isClosed ? 'Closed' : t.status ?? 'Open'}
                        tone={t.isClosed ? 'neutral' : 'good'}
                      />
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {fmtDateTime(t.created)}
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {t.closed ? fmtDateTime(t.closed) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

export default AdminTickets;
